import { validateAgainstSchema } from './schema.js';
import { KNOWN_STEP_VERBS } from './verbs.js';
import { CANONICAL_TOP_LEVEL_KEYS, RESERVED_ROLES, } from './types.js';
const KNOWN_TOP_LEVEL = new Set(CANONICAL_TOP_LEVEL_KEYS);
function isProse(value) {
    return typeof value === 'string' && value.length > 40 && /\s/.test(value);
}
function extractStepVerb(step) {
    if (step === null || step === undefined)
        return undefined;
    if (typeof step === 'string')
        return step.split(/[\s:]/, 1)[0];
    if (typeof step === 'object' && !Array.isArray(step)) {
        const keys = Object.keys(step);
        return keys[0];
    }
    return undefined;
}
export function validate(doc) {
    const errors = [];
    const warnings = [];
    const ok = validateAgainstSchema(doc);
    if (!ok && validateAgainstSchema.errors) {
        for (const err of validateAgainstSchema.errors) {
            errors.push({
                severity: 'error',
                code: `schema.${err.keyword}`,
                message: `${err.instancePath || '/'} ${err.message ?? 'invalid'}`,
                path: err.instancePath || '/',
            });
        }
    }
    // Unknown top-level keys → warning (tolerated per SPEC §1.3).
    for (const key of Object.keys(doc)) {
        if (!KNOWN_TOP_LEVEL.has(key)) {
            warnings.push({
                severity: 'warning',
                code: 'unknown-top-level-key',
                message: `Unknown top-level key \`${key}\` — tolerated, not indexed.`,
                path: `/${key}`,
            });
            if (isProse(doc[key])) {
                warnings.push({
                    severity: 'warning',
                    code: 'unembedded-prose',
                    message: `Prose under unknown key \`${key}\` will not be embedded. Move it into \`intent:\`, \`tags:\`, or an \`embedding-source\` file.`,
                    path: `/${key}`,
                });
            }
        }
    }
    // Unknown file-roles → warning (opaque per SPEC §2.4).
    if (doc.files && typeof doc.files === 'object') {
        for (const role of Object.keys(doc.files)) {
            if (!RESERVED_ROLES.has(role)) {
                warnings.push({
                    severity: 'warning',
                    code: 'unknown-file-role',
                    message: `File role \`${role}\` is not in the v0 reserved set — tools will surface it as-is.`,
                    path: `/files/${role}`,
                });
            }
        }
    }
    // Unknown step verbs in features + flows.
    const scanSteps = (steps, where) => {
        if (!Array.isArray(steps))
            return;
        steps.forEach((step, i) => {
            const verb = extractStepVerb(step);
            if (verb && !KNOWN_STEP_VERBS.has(verb)) {
                warnings.push({
                    severity: 'warning',
                    code: 'unknown-step-verb',
                    message: `Unknown step verb \`${verb}\` — not in v0 recognised set. Dogfood-driven expansion expected.`,
                    path: `${where}/steps/${i}`,
                });
            }
        });
    };
    if (Array.isArray(doc.features)) {
        doc.features.forEach((f, i) => scanSteps(f.steps, `/features/${i}`));
    }
    if (Array.isArray(doc.flows)) {
        doc.flows.forEach((f, i) => scanSteps(f.steps, `/flows/${i}`));
    }
    return { valid: errors.length === 0, errors, warnings };
}
//# sourceMappingURL=validate.js.map